/**
 * Results page
 * View and export all scraped places
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { Card, Space, Button, Tooltip, Select, Switch, Typography } from 'antd';
import { ReloadOutlined, ShareAltOutlined, EyeInvisibleOutlined, SwapOutlined, AppstoreOutlined, StopOutlined } from '@ant-design/icons';
import { PageHeader } from '@/components/common/PageHeader';
import {
  ResultsTable,
  ColumnSelector,
  FilterPanel,
  ExportMenu,
  PlaceModal,
  BulkActions,
  BulkRescanButton,
  ShareListModal,
} from '@/components/results';
import { usePlaces, useDeletePlaces, useCategories, usePlaceRescan, useSearchTerms, useCountries } from '@/hooks/usePlaces';
import { useExtensionUsers } from '@/hooks/useExtension';
import { getPlaces } from '@/services/placesApi';
import { useExport } from '@/hooks/useExport';
import { useNotification } from '@/stores/useNotificationStore';
import { useSiteAuth } from '@/stores/useSiteAuthStore';
import { useVisibleSearchTerms, useVisibleCategories, useNameBlacklistActive } from '@/hooks/useSettings';
import { STORAGE_KEYS } from '@/utils/constants';
import type { Place, PlaceFilter, PlaceListItem } from '@/types/place';

interface PersistedResultsState {
  filters: PlaceFilter;
  page: number;
  pageSize: number;
  selectedColumns: string[];
}

function loadPersistedResultsState(): Partial<PersistedResultsState> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.RESULTS_STATE);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore parse errors */ }
  return {};
}

function savePersistedResultsState(state: PersistedResultsState) {
  try {
    localStorage.setItem(STORAGE_KEYS.RESULTS_STATE, JSON.stringify(state));
  } catch { /* ignore storage errors */ }
}

const ALL_COLUMNS = [
  { key: 'name', label: 'Name', default: true },
  { key: 'category', label: 'Category', default: true },
  { key: 'rating', label: 'Rating', default: true },
  { key: 'reviewCount', label: 'Reviews', default: false },
  { key: 'address', label: 'Address', default: true },
  { key: 'phone', label: 'Phone', default: true },
  { key: 'website', label: 'Website', default: true },
  { key: 'coordinates', label: 'Coordinates', default: false },
  { key: 'priceLevel', label: 'Price', default: false },
  { key: 'plusCode', label: 'Plus Code', default: false },
  { key: 'openingHours', label: 'Hours', default: false },
  { key: 'ownerVerified', label: 'Verified', default: false },
  { key: 'jobId', label: 'Job ID', default: false },
  { key: 'scrapedAt', label: 'Scraped', default: false },
];

const { Text } = Typography;

// Simple seeded random number generator for consistent shuffling
function seededRandom(seed: number) {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
}

function shuffleArray<T>(array: T[], seed: number): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(seed + i) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export function ResultsPage() {
  const { warning } = useNotification();
  const { isAdmin } = useSiteAuth();

  // Load persisted results state once on mount
  const [persisted] = useState(loadPersistedResultsState);

  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    persisted.selectedColumns ?? ALL_COLUMNS.filter((c) => c.default).map((c) => c.key)
  );
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [filters, setFilters] = useState<PlaceFilter>(() => {
    const loaded = persisted.filters;
    if (!loaded) return { country: ['Germany'] };
    // Ensure country defaults to Germany if not set in persisted state (pre-migration)
    return { ...loaded, country: loaded.country ?? ['Germany'] };
  });
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Hide clicked by users filter
  const [hideClickedByUsers, setHideClickedByUsers] = useState<string[]>([]);

  // Scramble/randomize order - use localStorage for persistence
  const [scrambleEnabled, setScrambleEnabled] = useState(() => {
    return localStorage.getItem('results-scramble-enabled') === 'true';
  });
  const [scrambleSeed, setScrambleSeed] = useState(() => {
    const stored = localStorage.getItem('results-scramble-seed');
    if (stored) return parseInt(stored, 10);
    const seed = Date.now();
    localStorage.setItem('results-scramble-seed', seed.toString());
    return seed;
  });

  // Pagination state
  const [page, setPage] = useState(persisted.page ?? 1);
  const [pageSize, setPageSize] = useState(persisted.pageSize ?? 50);

  // Persist filters, pagination, and columns to localStorage
  useEffect(() => {
    savePersistedResultsState({ filters, page, pageSize, selectedColumns });
  }, [filters, page, pageSize, selectedColumns]);

  // Fetch extension users for "Hide Clicked By" filter
  const { data: extensionUsersData } = useExtensionUsers();
  const extensionUsers = extensionUsersData?.users || [];

  // Default to "All Users" once extension users have loaded
  const hideClickedInitialized = useRef(false);
  useEffect(() => {
    if (extensionUsers.length > 0 && !hideClickedInitialized.current) {
      hideClickedInitialized.current = true;
      setHideClickedByUsers(['__all__', ...extensionUsers.map(u => u.id)]);
    }
  }, [extensionUsers]);

  // Filter out __all__ marker to get actual user IDs for hide-clicked filtering
  const actualUserIds = hideClickedByUsers.filter(id => id !== '__all__');

  // Fetch visible search terms (for non-admin access control)
  const { data: visibleTermsList = [] } = useVisibleSearchTerms();
  const visibleTermsSet = useMemo(() => new Set(visibleTermsList), [visibleTermsList]);

  // Fetch visible categories (curated category filter)
  const { data: visibleCategoriesList = [] } = useVisibleCategories();

  // Name blacklist filter
  const { data: hasBlacklist = false } = useNameBlacklistActive();
  const [nameBlacklistEnabled, setNameBlacklistEnabled] = useState(() => {
    const stored = localStorage.getItem('results-name-blacklist-enabled');
    return stored === null ? true : stored === 'true';
  });

  const handleNameBlacklistToggle = (enabled: boolean) => {
    setNameBlacklistEnabled(enabled);
    localStorage.setItem('results-name-blacklist-enabled', enabled.toString());
    setPage(1);
  };

  // For non-admin users, blacklist is always active; for admins, it's togglable
  const useBlacklist = hasBlacklist && (isAdmin ? nameBlacklistEnabled : true);
  const hasCuratedCategories = visibleCategoriesList.length > 0;

  // Curated categories toggle - default ON, persisted to localStorage
  const [curatedCategoriesEnabled, setCuratedCategoriesEnabled] = useState(() => {
    const stored = localStorage.getItem('results-curated-categories-enabled');
    return stored === null ? true : stored === 'true';
  });

  const handleCuratedCategoriesToggle = (enabled: boolean) => {
    setCuratedCategoriesEnabled(enabled);
    localStorage.setItem('results-curated-categories-enabled', enabled.toString());
    setPage(1);
  };

  // For non-admin users, force searchTerm filter to visible terms only
  const effectiveSearchTerm = useMemo(() => {
    if (isAdmin) return filters.searchTerm;
    if (visibleTermsList.length === 0) return ['__none__']; // No visible terms = no results
    // If user selected specific terms, restrict to visible ones
    if (filters.searchTerm) {
      const userTerms = Array.isArray(filters.searchTerm) ? filters.searchTerm : [filters.searchTerm];
      const restricted = userTerms.filter((t) => visibleTermsSet.has(t));
      return restricted.length > 0 ? restricted : visibleTermsList;
    }
    return visibleTermsList;
  }, [isAdmin, filters.searchTerm, visibleTermsList, visibleTermsSet]);

  // Server-side curated categories filter
  const useCuratedFilter = hasCuratedCategories && curatedCategoriesEnabled;

  const {
    data: placesData,
    isLoading,
    isFetching,
    refetch,
  } = usePlaces({
    ...filters,
    curatedCategoriesOnly: useCuratedFilter || undefined,
    nameBlacklistActive: useBlacklist || undefined,
    searchTerm: effectiveSearchTerm,
    // Server-side exclusion: send user IDs, backend resolves clicked place IDs from MongoDB
    excludeClickedByUserIds: actualUserIds.length > 0 ? actualUserIds : undefined,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });

  // Fetch categories for the filter dropdown
  const { data: allCategories = [] } = useCategories();

  // Filter categories based on curated filter
  const categories = useMemo(() => {
    if (!hasCuratedCategories || !curatedCategoriesEnabled) return allCategories;
    return allCategories.filter((cat) => visibleCategoriesList.includes(cat));
  }, [allCategories, hasCuratedCategories, curatedCategoriesEnabled, visibleCategoriesList]);

  // Fetch countries with counts for the filter dropdown (respects active filters except country)
  const countryFilterParams = useMemo(() => {
    const { country: _country, ...rest } = filters;
    return {
      ...rest,
      curatedCategoriesOnly: useCuratedFilter || undefined,
      nameBlacklistActive: useBlacklist || undefined,
      searchTerm: effectiveSearchTerm,
      excludeClickedByUserIds: actualUserIds.length > 0 ? actualUserIds : undefined,
    };
  }, [filters, useCuratedFilter, useBlacklist, effectiveSearchTerm, actualUserIds]);
  const { data: countries = [] } = useCountries(countryFilterParams);

  // Fetch search terms for the filter dropdown (with remaining counts when hiding clicked)
  const searchTermParams = useMemo(() => ({
    excludeClickedByUserIds: actualUserIds.length > 0 ? actualUserIds : undefined,
    curatedCategoriesOnly: useCuratedFilter || undefined,
    nameBlacklistActive: useBlacklist || undefined,
  }), [actualUserIds, useCuratedFilter, useBlacklist]);
  const { data: allSearchTerms = [] } = useSearchTerms(searchTermParams);

  // Non-admins only see visible search terms
  const searchTerms = useMemo(() => {
    if (isAdmin) return allSearchTerms;
    return allSearchTerms.filter((t) => visibleTermsSet.has(t.searchQuery));
  }, [isAdmin, allSearchTerms, visibleTermsSet]);

  const deletePlaces = useDeletePlaces();
  const rescanPlace = usePlaceRescan();
  const { exportPlaces, isExporting } = useExport();

  // Handle filter changes - reset to page 1
  const handleFilterChange = (newFilters: PlaceFilter) => {
    setFilters(newFilters);
    setPage(1);
  };

  // Clear all filters handler — resets country to default 'Germany'
  const handleClearFilters = () => {
    setFilters({ country: ['Germany'] });
    setPage(1);
  };

  const handleExport = async (format: 'csv' | 'xlsx' | 'json') => {
    // If specific rows are selected, export only those from current page
    if (selectedRows.length > 0) {
      const selectedPlaces = placesData?.places.filter((p) => selectedRows.includes(p.id)) || [];
      if (selectedPlaces.length === 0) {
        warning('No data', 'No places selected');
        return;
      }
      await exportPlaces(selectedPlaces, format, selectedColumns);
      return;
    }

    // Otherwise, fetch ALL places matching current filters (not just current page)
    const totalCount = placesData?.total || 0;
    if (totalCount === 0) {
      warning('No data', 'No places to export');
      return;
    }

    // Fetch all filtered results with high limit
    const allPlacesResponse = await getPlaces({
      ...filters,
      limit: 100000, // High limit to get all results
      offset: 0,
    });

    if (allPlacesResponse.places.length === 0) {
      warning('No data', 'No places to export');
      return;
    }

    // Hook (useExport/useClientExport) already shows notifications
    await exportPlaces(allPlacesResponse.places, format, selectedColumns);
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;

    // Hook (useDeletePlaces) already shows notifications
    await deletePlaces.mutateAsync(selectedRows);
    setSelectedRows([]);
    refetch();
  };

  // Handle rescan - uses the place's database ID
  const handleRescan = (place: PlaceListItem) => {
    rescanPlace.mutate(place.id);
  };

  // Fetch all filtered places for bulk rescan
  const fetchAllFilteredPlaces = async (): Promise<PlaceListItem[]> => {
    const response = await getPlaces({
      ...filters,
      limit: 100000, // High limit to get all results
      offset: 0,
    });
    return response.places;
  };

  // Fetch only selected places for bulk rescan
  const fetchSelectedPlaces = async (): Promise<PlaceListItem[]> => {
    // Get all filtered places and filter to selected ones
    // Note: selectedRows contains placeId values (Google Place IDs), not database IDs
    const response = await getPlaces({
      ...filters,
      limit: 100000,
      offset: 0,
    });
    return response.places.filter((p) => selectedRows.includes(p.placeId));
  };

  const rawPlaces = placesData?.places || [];

  // Handle scramble toggle - persist to localStorage
  const handleScrambleToggle = (enabled: boolean) => {
    setScrambleEnabled(enabled);
    localStorage.setItem('results-scramble-enabled', enabled.toString());
    if (enabled) {
      // Generate new seed when enabling - update both localStorage and React state
      const newSeed = Date.now();
      localStorage.setItem('results-scramble-seed', newSeed.toString());
      setScrambleSeed(newSeed);
    }
  };

  // Apply scramble (hide-clicked is now handled server-side via excludePlaceIds)
  const places = useMemo(() => {
    let result = rawPlaces;

    // Apply scramble if enabled (use page offset + seed for consistency)
    if (scrambleEnabled) {
      result = shuffleArray(result, scrambleSeed + page);
    }

    return result;
  }, [rawPlaces, scrambleEnabled, scrambleSeed, page]);

  return (
    <div>
      <PageHeader
        title="Results"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
            >
              Refresh
            </Button>
            {isAdmin && (
              <ExportMenu
                onExport={handleExport}
                loading={isExporting}
                disabled={places.length === 0}
              />
            )}
          </Space>
        }
      />

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Toolbar */}
        <Card size="small">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Row 1: Filter controls */}
            <FilterPanel
              filters={filters}
              onChange={handleFilterChange}
              onClear={handleClearFilters}
              categories={categories}
              countries={countries}
              searchTerms={searchTerms}
              loading={isFetching}
            />

            {/* Row 2: Toggles + Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {/* Hide Clicked By filter */}
              {extensionUsers.length > 0 && (
                <Tooltip title="Hide places clicked by selected users">
                  <Select
                    mode="multiple"
                    placeholder={
                      <Space size={4}>
                        <EyeInvisibleOutlined />
                        <span>Hide Clicked</span>
                      </Space>
                    }
                    value={hideClickedByUsers}
                    onChange={(values) => {
                      // Check if "all" was just selected
                      if (values.includes('__all__') && !hideClickedByUsers.includes('__all__')) {
                        // Select all users
                        setHideClickedByUsers(['__all__', ...extensionUsers.map(u => u.id)]);
                      } else if (!values.includes('__all__') && hideClickedByUsers.includes('__all__')) {
                        // "All" was deselected, clear everything
                        setHideClickedByUsers([]);
                      } else if (values.includes('__all__') && values.length <= extensionUsers.length) {
                        // A user was deselected while "all" is selected, remove "all"
                        setHideClickedByUsers(values.filter(v => v !== '__all__'));
                      } else {
                        setHideClickedByUsers(values);
                      }
                      setPage(1);
                    }}
                    options={[
                      { value: '__all__', label: '— All Users —' },
                      ...extensionUsers.map((user) => ({
                        value: user.id,
                        label: user.name,
                      })),
                    ]}
                    style={{ width: 200 }}
                    allowClear
                    showSearch
                    maxTagCount={1}
                    optionFilterProp="label"
                  />
                </Tooltip>
              )}

              {/* Curated categories toggle */}
              <Tooltip title={
                !hasCuratedCategories
                  ? 'No curated categories configured (set up in Settings → Categories)'
                  : curatedCategoriesEnabled
                    ? 'Curated categories filter ON — showing only admin-selected categories'
                    : 'Curated categories filter OFF — showing all categories'
              }>
                <Space size={4} style={{
                  background: curatedCategoriesEnabled && hasCuratedCategories ? 'var(--ant-color-primary-bg)' : undefined,
                  padding: '4px 8px',
                  borderRadius: 4,
                  opacity: hasCuratedCategories ? 1 : 0.5,
                }}>
                  <AppstoreOutlined style={{ color: curatedCategoriesEnabled && hasCuratedCategories ? 'var(--ant-color-primary)' : undefined }} />
                  <Switch
                    size="small"
                    checked={curatedCategoriesEnabled && hasCuratedCategories}
                    onChange={handleCuratedCategoriesToggle}
                    disabled={!hasCuratedCategories}
                  />
                  {curatedCategoriesEnabled && hasCuratedCategories && <Text type="secondary" style={{ fontSize: 11 }}>Curated</Text>}
                </Space>
              </Tooltip>

              {/* Name blacklist toggle - admin only */}
              {isAdmin && (
                <Tooltip title={
                  !hasBlacklist
                    ? 'No blacklist patterns configured (set up in Settings → Name Blacklist)'
                    : nameBlacklistEnabled
                      ? 'Name blacklist filter ON — hiding blacklisted places'
                      : 'Name blacklist filter OFF — showing all places'
                }>
                  <Space size={4} style={{
                    background: nameBlacklistEnabled && hasBlacklist ? 'var(--ant-color-primary-bg)' : undefined,
                    padding: '4px 8px',
                    borderRadius: 4,
                    opacity: hasBlacklist ? 1 : 0.5,
                  }}>
                    <StopOutlined style={{ color: nameBlacklistEnabled && hasBlacklist ? 'var(--ant-color-primary)' : undefined }} />
                    <Switch
                      size="small"
                      checked={nameBlacklistEnabled && hasBlacklist}
                      onChange={handleNameBlacklistToggle}
                      disabled={!hasBlacklist}
                    />
                    {nameBlacklistEnabled && hasBlacklist && <Text type="secondary" style={{ fontSize: 11 }}>Blacklist</Text>}
                  </Space>
                </Tooltip>
              )}

              {/* Scramble toggle */}
              <Tooltip title={scrambleEnabled ? 'Randomized order is ON' : 'Enable random order'}>
                <Space size={4} style={{
                  background: scrambleEnabled ? 'var(--ant-color-primary-bg)' : undefined,
                  padding: '4px 8px',
                  borderRadius: 4,
                }}>
                  <SwapOutlined style={{ color: scrambleEnabled ? 'var(--ant-color-primary)' : undefined }} />
                  <Switch
                    size="small"
                    checked={scrambleEnabled}
                    onChange={handleScrambleToggle}
                  />
                  {scrambleEnabled && <Text type="secondary" style={{ fontSize: 11 }}>Shuffled</Text>}
                </Space>
              </Tooltip>

              <ColumnSelector
                columns={ALL_COLUMNS}
                selected={selectedColumns}
                onChange={setSelectedColumns}
              />

              {/* Spacer */}
              <div style={{ flex: 1 }} />

              {/* Actions */}
              {isAdmin && (
                <BulkRescanButton
                  fetchPlaces={selectedRows.length > 0 ? fetchSelectedPlaces : fetchAllFilteredPlaces}
                  loading={isFetching}
                  disabled={places.length === 0}
                  totalCount={selectedRows.length > 0 ? selectedRows.length : placesData?.total || 0}
                  selectedCount={selectedRows.length}
                />
              )}
              {isAdmin && (
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() => setShareModalOpen(true)}
                  disabled={places.length === 0}
                >
                  Share{selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
                </Button>
              )}
              {isAdmin && selectedRows.length > 0 && (
                <BulkActions
                  selectedCount={selectedRows.length}
                  totalCount={placesData?.total}
                  onDelete={handleBulkDelete}
                  onClearSelection={() => setSelectedRows([])}
                  loading={deletePlaces.isPending}
                />
              )}
            </div>
          </div>
        </Card>

        {/* Results table */}
        <ResultsTable
          places={places}
          loading={isLoading}
          visibleColumns={selectedColumns}
          selectedRowKeys={isAdmin ? selectedRows : undefined}
          onSelectionChange={isAdmin ? setSelectedRows : undefined}
          onPlaceClick={setSelectedPlace}
          onRescan={isAdmin ? handleRescan : undefined}
          rescanningId={isAdmin && rescanPlace.isPending ? rescanPlace.variables : null}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: placesData?.total || 0,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            },
          }}
        />
      </Space>

      {/* Place details modal */}
      <PlaceModal
        place={selectedPlace}
        open={!!selectedPlace}
        onClose={() => setSelectedPlace(null)}
      />

      {/* Share list modal */}
      <ShareListModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        filters={filters}
        visibleColumns={selectedColumns}
        selectedPlaceIds={selectedRows.length > 0 ? selectedRows : undefined}
        totalCount={placesData?.total}
      />
    </div>
  );
}

export default ResultsPage;
